/**
 * Every platform setting a sys admin can change on Configuration → Platform
 * (060), described once.
 *
 * The screen renders from this list, the API validates against it, and the
 * server reads through it (`platformValues.ts`) — so adding a setting is an
 * entry here and one read where it is used, not a migration and a form.
 *
 * WHAT IS HERE is what an admin would naturally change — above all the
 * model, because models change and switching one must not need a deploy.
 * WHAT IS NOT: integration plumbing between our own services (the send
 * webhook, the action executor), tuning limits, database connections, ports
 * and the sign-in registration. Those stay deployment settings; promoting
 * one is an entry here and a read where it is used.
 */

export type PlatformSettingType = 'text' | 'secret' | 'url' | 'email' | 'bool' | 'int' | 'number' | 'enum' | 'prompt';

export interface PlatformSettingSpec {
  key: string;
  group: PlatformSettingGroup;
  label: string;
  /** What changing it does, for the person looking at the box. */
  help: string;
  type: PlatformSettingType;
  /** The environment variable it falls back to, for the screen to name. */
  env: string;
  options?: readonly { value: string; label: string }[];
  min?: number;
  max?: number;
  placeholder?: string;
  /**
   * False for a deployment setting shown for reference only: needed before
   * the application can start (a database connection, the sign-in
   * registration), so it cannot be changed from a screen the application
   * serves. Shown so a sys admin can see what is set without a deploy log.
   */
  editable?: boolean;
}

export type PlatformSettingGroup = 'model' | 'gateway' | 'mail' | 'features' | 'environment';

export const PLATFORM_SETTING_GROUPS: readonly { key: PlatformSettingGroup; title: string; blurb: string }[] = [
  {
    key: 'model',
    title: 'Model',
    blurb:
      'The language model behind suggested answers, the intake guardrails, drafted articles and pattern mining. Models change: switch here, test, and it applies within a minute. Without one, those features fall back or switch off; nothing else stops.',
  },
  {
    key: 'gateway',
    title: 'Model gateway',
    blurb:
      'Send model calls through a gateway such as LiteLLM instead of straight to the provider — for every job, or only the ones you pick. Off by default; the Model settings above keep working exactly as they are, and switching the gateway off goes straight back to them. Test it before switching it on.',
  },
  {
    key: 'mail',
    title: 'Outbound mail',
    blurb: 'Whether Tributary emails anyone at all, and testing mode for trying it safely.',
  },
  { key: 'features', title: 'Platform switches', blurb: 'Behaviour that applies to every group.' },
  {
    key: 'environment',
    title: 'Environment',
    blurb:
      'Everything else the deployment sets. Most can be overridden here; the ones needed before the application can start are shown for reference only.',
  },
]

/** The jobs that call a model, each of which may use its own (see `ai.modelFor.*`). */
export const AI_PURPOSES = [
  { key: 'intake', label: 'intake', help: 'Screening each email, and answering it from the knowledgebase.' },
  { key: 'summary', label: 'ticket summaries', help: 'The three-line summary at the top of a ticket.' },
  { key: 'knowledge', label: 'knowledge', help: 'Drafting articles, AI review, and capturing what fixed a ticket.' },
  { key: 'mining', label: 'pattern mining', help: 'Finding repeat problems across tickets.' },
  { key: 'suggest', label: 'suggested answers', help: 'Judging which articles fit a ticket.' },
  { key: 'actions', label: 'actions', help: 'Reading a request for an action it could take.' },
  { key: 'proof', label: 'AI Proof', help: 'Proofreading a reply or note an agent has written, on request.' },
  { key: 'replies', label: 'replies', help: 'Reading a reply to a resolved ticket (thanks, done, or still a problem), and writing the "you\'re welcome".' },
  { key: 'drafts', label: 'suggested replies', help: "Suggesting a reply in the agent's own style." },
] as const;
export type AiPurpose = (typeof AI_PURPOSES)[number]['key'];

export interface ModelPrice {
  inputPerMillion: number;
  cachedInputPerMillion: number;
  outputPerMillion: number;
}

/**
 * The price table setting, parsed: "name in cached out; name in cached out".
 * Returns the problem instead when an entry is not four parts with three
 * non-negative numbers.
 */
export function parsePriceTable(raw: string): { prices: Record<string, ModelPrice> } | { problem: string } {
  const prices: Record<string, ModelPrice> = {};
  for (const entry of raw.split(';').map((e) => e.trim()).filter(Boolean)) {
    const parts = entry.split(/\s+/);
    const nums = parts.slice(1).map(Number);
    if (parts.length !== 4 || nums.some((n) => !Number.isFinite(n) || n < 0)) {
      return { problem: `"${entry}" should be a model name and three prices, e.g. gpt-6-luna 0.01 0.001 0.5` };
    }
    prices[parts[0]!.toLowerCase()] = { inputPerMillion: nums[0]!, cachedInputPerMillion: nums[1]!, outputPerMillion: nums[2]! };
  }
  return { prices };
}

/**
 * How AI Proof should make a draft read, when the platform has not said.
 * Professional: casual writing is lifted into a clear, courteous message.
 */
export const DEFAULT_PROOF_STYLE = [
  'Make the message professional: clear, courteous and plain, as an experienced service desk would write it.',
  'Rewrite casual writing into full sentences with correct spelling, grammar and punctuation -- no slang, no text-speak ("thx", "u", "pls"), no emoji.',
  'For a reply to a requester: warm but not chatty, and address them by name if the draft does.',
  'For a private note: concise and factual; it is for colleagues.',
  'Keep it about the same length. Shorter is fine; do not pad it.',
].join('\n');

export const PLATFORM_SETTINGS: readonly PlatformSettingSpec[] = [
  // --- model ---------------------------------------------------------------
  {
    key: 'ai.provider',
    group: 'model',
    label: 'Provider',
    help: 'Off switches every model feature off without removing the key.',
    type: 'enum',
    env: 'AI_PROVIDER',
    options: [
      { value: 'openai', label: 'OpenAI-compatible' },
      { value: 'none', label: 'Off' },
    ],
  },
  { key: 'ai.apiKey', group: 'model', label: 'API key', help: 'Never shown again once saved.', type: 'secret', env: 'AI_API_KEY' },
  { key: 'ai.model', group: 'model', label: 'Model', help: 'The model name the provider expects.', type: 'text', env: 'AI_MODEL' },
  {
    key: 'ai.baseUrl',
    group: 'model',
    label: 'API address',
    help: 'Change only for a proxy or a compatible provider.',
    type: 'url',
    env: 'AI_BASE_URL',
  },
  {
    key: 'ai.effort',
    group: 'model',
    label: 'Effort',
    help: 'Default reasoning effort. Low is the cheap setting and enough for summaries and screening.',
    type: 'enum',
    env: 'AI_EFFORT',
    options: ['none', 'low', 'medium', 'high'].map((v) => ({ value: v, label: v })),
  },
  {
    key: 'ai.timeoutMs',
    group: 'model',
    label: 'Timeout',
    help: 'Milliseconds before a model call is given up on.',
    type: 'int',
    env: 'AI_TIMEOUT_MS',
    min: 1000,
    max: 300_000,
  },
  // A model AND an effort per job: models move fast, and the cheapest one
  // that does a job well is not the same for screening mail as for drafting
  // an article -- nor is how hard it should think. Pattern mining earns a
  // long think; reading "thanks!" does not. Blank means the setting above.
  ...AI_PURPOSES.flatMap((p): PlatformSettingSpec[] => [
    {
      key: `ai.modelFor.${p.key}`,
      group: 'model',
      label: `Model for ${p.label}`,
      help: `Blank uses the model above. ${p.help}`,
      type: 'text',
      env: `AI_MODEL_${p.key.toUpperCase()}`,
    },
    {
      key: `ai.effortFor.${p.key}`,
      group: 'model',
      label: `Effort for ${p.label}`,
      help: 'Blank uses the effort above.',
      type: 'enum',
      env: `AI_EFFORT_${p.key.toUpperCase()}`,
      options: ['none', 'low', 'medium', 'high'].map((v) => ({ value: v, label: v })),
      placeholder: 'same as the effort above',
    },
  ]),
  // The one part of a prompt a person may change. AI Proof's style -- how a
  // draft should read -- lives here; what it may never do (change facts,
  // add promises) stays in code and is always appended, so an edit here can
  // make the tone wrong but cannot make a reply untrue.
  {
    key: 'ai.proofStyle',
    group: 'model',
    label: 'AI Proof: how drafts should read',
    help: 'The style AI Proof rewrites a reply or note into. Blank uses the default shown. Whatever this says, AI Proof never changes facts, names, numbers, links or steps, and never adds information or promises -- those rules are fixed.',
    type: 'prompt',
    env: 'AI_PROOF_STYLE',
    placeholder: DEFAULT_PROOF_STYLE,
  },
  {
    key: 'ai.prices',
    group: 'model',
    label: 'Prices (US$ per million tokens)',
    help: 'What each model costs, so the cost figures follow the model that actually ran: name, input, cached input, output — one model per entry, separated by semicolons. A model not listed is priced as gpt-5.6-luna.',
    type: 'text',
    env: 'AI_PRICES',
    placeholder: 'gpt-6-luna 0.01 0.001 0.5; gpt-5.6-luna 0.2 0.02 1.2',
  },

  // --- model gateway (LiteLLM) -------------------------------------------------
  // Separate from the direct settings above, never instead of them: the
  // gateway is something to try, and turning it off must put back exactly
  // what ran before, without anyone re-entering a model name.
  {
    key: 'ai.gateway.mode',
    group: 'gateway',
    label: 'Use the gateway',
    help: 'Off: every model call goes straight to the provider, as before. Every job: all of them through the gateway. Only selected jobs: just the ones switched on below.',
    type: 'enum',
    env: 'AI_GATEWAY_MODE',
    options: [
      { value: 'off', label: 'Off' },
      { value: 'all', label: 'Every job' },
      { value: 'selected', label: 'Only selected jobs' },
    ],
  },
  {
    key: 'ai.gateway.baseUrl',
    group: 'gateway',
    label: 'Gateway address',
    help: 'The gateway’s OpenAI-compatible address, e.g. https://litellm.example/v1.',
    type: 'url',
    env: 'AI_GATEWAY_BASE_URL',
  },
  { key: 'ai.gateway.apiKey', group: 'gateway', label: 'Gateway key', help: 'The gateway’s key for Tributary (a LiteLLM virtual key). Never shown again once saved.', type: 'secret', env: 'AI_GATEWAY_API_KEY' },
  {
    key: 'ai.gateway.model',
    group: 'gateway',
    label: 'Gateway model',
    help: 'The model name as the gateway knows it. Blank uses the model names from the Model settings.',
    type: 'text',
    env: 'AI_GATEWAY_MODEL',
  },
  ...AI_PURPOSES.flatMap((p): PlatformSettingSpec[] => [
    {
      key: `ai.gateway.use.${p.key}`,
      group: 'gateway',
      label: `Gateway for ${p.label}`,
      help: `Only when "Use the gateway" is "Only selected jobs". ${p.help}`,
      type: 'bool',
      env: `AI_GATEWAY_USE_${p.key.toUpperCase()}`,
    },
    {
      key: `ai.gateway.modelFor.${p.key}`,
      group: 'gateway',
      label: `Gateway model for ${p.label}`,
      help: 'Blank uses the gateway model above, then this job’s model from the Model settings.',
      type: 'text',
      env: `AI_GATEWAY_MODEL_${p.key.toUpperCase()}`,
    },
  ]),

  // --- outbound mail ---------------------------------------------------------
  {
    key: 'mail.notificationsEnabled',
    group: 'mail',
    label: 'Send email',
    help: 'Off: nothing is emailed to anyone — replies, declines, notifications. Queued mail is marked not sent, not held.',
    type: 'bool',
    env: 'NOTIFICATIONS_ENABLED',
  },
  {
    key: 'mail.sendVia',
    group: 'mail',
    label: 'Send through',
    help:
      'Microsoft Graph sends from each desk’s own mailbox — replies thread under the person’s email — using the app on Mail reading, which needs Mail.Send. The n8n webhook is the old way, kept until every deployment has moved.',
    type: 'enum',
    env: 'MAIL_SEND_VIA',
    options: [
      { value: 'graph', label: 'Microsoft Graph — the desk’s mailbox' },
      { value: 'webhook', label: 'n8n send webhook (old)' },
    ],
  },
  {
    key: 'mail.testing',
    group: 'mail',
    label: 'Testing mode',
    help: 'On: every email is redirected to the address below instead of the real recipient.',
    type: 'bool',
    env: 'TESTING',
  },
  {
    key: 'mail.testingRedirectTo',
    group: 'mail',
    label: 'Testing redirect address',
    help: 'Where all mail goes in testing mode. A person, never a desk mailbox.',
    type: 'email',
    env: 'TESTING_REDIRECT_TO',
  },


  // --- switches ----------------------------------------------------------------------
  {
    key: 'features.routingEngine',
    group: 'features',
    label: 'Routing engine',
    help: 'Route new tickets by each group’s routing rules.',
    type: 'bool',
    env: 'ROUTING_ENGINE_ENABLED',
  },



  // --- environment: overridable ---------------------------------------------------
  { key: 'mail.sendWebhookUrl', group: 'environment', label: 'Mail send webhook', help: 'The n8n workflow that sends mail, when Outbound mail → Send through is the webhook.', type: 'url', env: 'MAIL_WEBHOOK_URL' },
  { key: 'mail.sendWebhookSecret', group: 'environment', label: 'Mail send signing secret', help: 'Signs every send and verifies the delivery callback. Must match the n8n side.', type: 'secret', env: 'TRIBUTARY_MAIL_HMAC_SECRET' },
  { key: 'mail.defaultFrom', group: 'environment', label: 'Default desk address', help: 'Used when a group has not set its own desk mailbox.', type: 'email', env: 'SMTP_FROM', placeholder: 'ESA Service Desk <servicedesk@esassoc.com>' },
  { key: 'actions.executorUrl', group: 'environment', label: 'Action executor', help: 'Empty: authorized actions wait rather than run.', type: 'url', env: 'ACTION_EXECUTOR_URL' },
  { key: 'actions.executorSecret', group: 'environment', label: 'Action executor signing secret', help: 'Signs each action so the executor can refuse one that was not authorized.', type: 'secret', env: 'ACTION_EXECUTOR_SECRET' },
  { key: 'features.requireCleanScan', group: 'environment', label: 'Require a clean attachment scan', help: 'Refuse to hand out a file until it has been scanned. Only with a scanner in place.', type: 'bool', env: 'REQUIRE_CLEAN_SCAN' },
  { key: 'limits.attachmentMaxBytes', group: 'environment', label: 'Largest attachment (bytes)', help: 'Larger files are refused on upload and left out of email.', type: 'int', env: 'ATTACHMENT_MAX_BYTES', min: 1024, max: 200 * 1024 * 1024 },
  { key: 'limits.suggestMinScore', group: 'environment', label: 'Default suggestion floor (0–1)', help: 'For groups that have not set their own on the Knowledge tab.', type: 'number', env: 'KB_SUGGEST_MIN_SCORE', min: 0, max: 1 },
  { key: 'rag.maxBatch', group: 'environment', label: 'Knowledgebase batch size', help: 'Documents per push. Must not exceed the server’s own cap.', type: 'int', env: 'RAG_MAX_BATCH', min: 1, max: 5000 },
  { key: 'rag.maxDeleteRatio', group: 'environment', label: 'Knowledgebase delete guard (0–1)', help: 'Share of a source a sync may delete before it is refused. Mirrors the server.', type: 'number', env: 'RAG_MAX_DELETE_RATIO', min: 0.01, max: 1 },
  { key: 'rag.timeoutMs', group: 'environment', label: 'Knowledgebase timeout (ms)', help: 'Before a knowledgebase call is given up on.', type: 'int', env: 'RAG_TIMEOUT_MS', min: 1000, max: 300_000 },
  { key: 'graph.appTenantId', group: 'environment', label: 'Directory token: tenant ID', help: 'The app that mints the Graph MCP token. Empty: the sign-in registration’s tenant.', type: 'text', env: 'GRAPH_AZURE_AD_TENANT_ID' },
  { key: 'graph.appClientId', group: 'environment', label: 'Directory token: client ID', help: 'Empty: the sign-in registration. Needs User.Read.All.', type: 'text', env: 'GRAPH_AZURE_AD_CLIENT_ID' },
  { key: 'graph.appClientSecret', group: 'environment', label: 'Directory token: client secret', help: 'Required when a separate application is named.', type: 'secret', env: 'GRAPH_AZURE_AD_SECRET' },

  // --- environment: deployment only, shown for reference ---------------------------
  { key: 'deploy.databaseUrl', group: 'environment', label: 'Database (application)', help: 'Deployment only.', type: 'secret', env: 'DATABASE_URL', editable: false },
  { key: 'deploy.databaseOwnerUrl', group: 'environment', label: 'Database (owner, migrations)', help: 'Deployment only.', type: 'secret', env: 'DATABASE_OWNER_URL', editable: false },
  { key: 'deploy.webOrigin', group: 'environment', label: 'Web address', help: 'Deployment only.', type: 'url', env: 'PUBLIC_WEB_ORIGIN', editable: false },
  { key: 'deploy.authProvider', group: 'environment', label: 'Sign-in', help: 'Deployment only.', type: 'text', env: 'AUTH_PROVIDER', editable: false },
  { key: 'deploy.signInTenantId', group: 'environment', label: 'Sign-in tenant ID', help: 'Deployment only.', type: 'text', env: 'AZURE_AD_TENANT_ID', editable: false },
  { key: 'deploy.signInClientId', group: 'environment', label: 'Sign-in client ID', help: 'Deployment only.', type: 'text', env: 'AZURE_AD_CLIENT_ID', editable: false },
  { key: 'deploy.nodeEnv', group: 'environment', label: 'Runtime', help: 'Deployment only.', type: 'text', env: 'NODE_ENV', editable: false },
];

export function platformSettingSpec(key: string): PlatformSettingSpec | null {
  return PLATFORM_SETTINGS.find((s) => s.key === key) ?? null;
}

/**
 * Is this a value the setting can hold? Returns the problem, or null.
 *
 * The same check runs in the browser (before save) and on the server (on
 * save), so the screen can never offer to save what the server will refuse.
 */
export function platformSettingProblem(spec: PlatformSettingSpec, raw: string): string | null {
  const v = raw.trim();
  switch (spec.type) {
    case 'bool':
      return v === 'true' || v === 'false' ? null : 'must be on or off';
    case 'int':
    case 'number': {
      const n = Number(v);
      if (v === '' || !Number.isFinite(n)) return 'must be a number';
      if (spec.type === 'int' && !Number.isInteger(n)) return 'must be a whole number';
      if (spec.min !== undefined && n < spec.min) return `must be at least ${spec.min}`;
      if (spec.max !== undefined && n > spec.max) return `must be at most ${spec.max}`;
      return null;
    }
    case 'url':
      return /^https?:\/\/[^\s]+$/i.test(v) ? null : 'must be an http or https address';
    case 'email':
      return /^([^<>@\s][^<>@]*<)?[^\s<>@]+@[^\s<>@]+\.[^\s<>@]+>?$/.test(v) ? null : 'must be an email address, as "Name <a@b>" or "a@b"';
    case 'enum':
      return spec.options?.some((o) => o.value === v) ? null : `must be one of ${spec.options?.map((o) => o.value).join(', ')}`;
    case 'prompt':
      return v.length > 4000 ? 'is too long (4000 characters at most)' : null;
    case 'text':
    case 'secret': {
      if (v.length > 1000) return 'is too long';
      if (spec.key === 'ai.prices') {
        const t = parsePriceTable(v);
        return 'problem' in t ? t.problem : null;
      }
      return null;
    }
  }
}
